// =============================================================================
// GifPicker — searchable Giphy picker that posts the chosen GIF.
// =============================================================================
// Pops out from the composer GIF button. Shows trending GIFs by default,
// switches to search results as the user types. Clicking a GIF posts it
// to the current channel (server downloads + stores as an attachment) and
// closes the picker.
//
// The picker grid uses the small "fixed_height_small" preview from
// Giphy (~100px tall, tens of KB each) — fast to load, easy on Giphy
// rate limits. The full-size original is downloaded server-side only
// when the user actually picks one.
//
// Loading the picker before the user opens it would burn bandwidth and
// API calls; we only fetch when mounted, debouncing search input by
// 250ms. Trending is fetched once and cached for the lifetime of the
// open picker (closing/reopening re-fetches).
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { api, ApiError } from '../api';
import { Spinner } from './ui';

interface SlimGif {
  id: string;
  title: string;
  previewUrl: string;
  previewWidth: number;
  previewHeight: number;
  fullUrl: string;
  fullWidth: number;
  fullHeight: number;
}

interface Props {
  onPick: (gif: SlimGif) => Promise<void> | void;
  onClose: () => void;
}

export function GifPicker({ onPick, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [gifs, setGifs] = useState<SlimGif[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when the user clicks a GIF — disables the grid so they can't
  // double-click and post twice while the upload is in flight.
  const [posting, setPosting] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Esc closes; auto-focus search.
  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Debounce the search input — 250ms is fast enough to feel responsive,
  // slow enough to coalesce rapid typing and not hammer the Giphy quota.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(h);
  }, [search]);

  // Fetch trending or search depending on debounced query.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const promise = debounced
      ? api.gifsSearch(debounced)
      : api.gifsTrending();
    promise
      .then((res) => {
        if (cancelled) return;
        setGifs(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 503) {
          setError('GIF picker is not configured on this server.');
        } else {
          setError(err instanceof ApiError ? err.message : 'Failed to load GIFs');
        }
        setGifs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const handlePick = useCallback(
    async (gif: SlimGif) => {
      if (posting) return;
      setPosting(gif.id);
      try {
        await onPick(gif);
        onClose();
      } catch (err) {
        // Pick failed (network, server error). Surface to user, don't close.
        setError(err instanceof ApiError ? err.message : 'Failed to post GIF');
        setPosting(null);
      }
    },
    [posting, onPick, onClose]
  );

  return (
    <div
      className="hybrid-card w-80 h-[420px] flex flex-col"
      role="dialog"
      aria-label="GIF picker"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="hybrid-card-header py-1.5!">
        <span className="text-[11px]">Pick a GIF</span>
        <span className="text-[9px] opacity-60 ml-1">via GIPHY</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="ml-auto text-[12px] leading-none w-5 h-5 hover:bg-white/10 rounded-sm flex items-center justify-center"
        >
          ×
        </button>
      </div>

      <div className="p-2 flex flex-col flex-1 min-h-0 bg-surface">
        <div className="relative mb-2">
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search GIPHY…"
            className="w-full bevel-down rounded-aim bg-bg px-2 py-1 text-sm outline-hidden focus:ring-1! focus:ring-accent"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 text-text-subtle hover:text-text rounded-sm flex items-center justify-center"
            >
              ×
            </button>
          )}
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <Spinner size={16} />
          </div>
        )}

        {!loading && error && (
          <div className="flex-1 flex items-center justify-center px-3 text-center text-xs text-text-muted">
            {error}
          </div>
        )}

        {!loading && !error && gifs.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-xs text-text-subtle">
            No GIFs found.
          </div>
        )}

        {!loading && !error && gifs.length > 0 && (
          // Two-column masonry-ish grid. We fix the column count and let
          // each tile take its natural aspect ratio — Giphy's previews
          // are all the same height so this packs cleanly. CSS columns
          // would give true masonry but the simpler grid is plenty for
          // a picker of this size.
          <div className="flex-1 overflow-y-auto -mx-1">
            <div className="grid grid-cols-2 gap-1 px-1">
              {gifs.map((gif) => {
                const isPosting = posting === gif.id;
                return (
                  <button
                    key={gif.id}
                    type="button"
                    onClick={() => { void handlePick(gif); }}
                    disabled={!!posting}
                    title={gif.title || 'GIF'}
                    className={`relative block w-full bevel-down rounded-aim overflow-hidden bg-bg transition-opacity ${
                      posting && !isPosting ? 'opacity-40' : 'hover:opacity-90'
                    }`}
                    style={{
                      // Reserve aspect ratio so the grid doesn't reflow as
                      // images load.
                      aspectRatio:
                        gif.previewWidth && gif.previewHeight
                          ? `${gif.previewWidth} / ${gif.previewHeight}`
                          : '4 / 3',
                    }}
                  >
                    <img
                      src={gif.previewUrl}
                      alt={gif.title || 'GIF'}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                    {isPosting && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Spinner size={16} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
